import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const faqItems = t('faq.items', { returnObjects: true });

  // Generate FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-black"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* FAQ Schema for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema).replace(/</g, '\\u003c') }}
      />

      {/* Background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="relative z-10 max-w-[900px] mx-auto px-5 md:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div
            className={`inline-flex items-center gap-3 mb-6 opacity-0 ${
              isVisible ? 'animate-fade-in-up' : ''
            }`}
          >
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">
              {t('nav.faq')}
            </span>
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
          </div>

          <h2
            className={`display-large text-white mb-4 opacity-0 ${
              isVisible ? 'animate-fade-in-up delay-100' : ''
            }`}
          >
            {t('faq.title')}
          </h2>
          <p
            className={`text-white/60 text-lg opacity-0 ${
              isVisible ? 'animate-fade-in-up delay-200' : ''
            }`}
          >
            {t('faq.subtitle')}
          </p>
        </div>

        {/* FAQ Accordion */}
        <div
          className={`opacity-0 ${
            isVisible ? 'animate-fade-in-up delay-300' : ''
          }`}
        >
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={item.question}
                value={`item-${index}`}
                className="bg-white/[0.02] border border-white/10 px-6 hover:border-[#00FFD1]/30 transition-colors duration-300"
              >
                <AccordionTrigger className="text-white hover:text-[#00FFD1] py-6 text-left">
                  <span className="pr-4 text-base md:text-lg font-medium">
                    {item.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-white/70 pb-6 text-sm md:text-base leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
